import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';

import { ResourceItem, TextAlignment } from '@ppg/core/enums';
import { ColorInfoInvalid, ColorInfoValid, CuratedColorPaletteContent } from '@ppg/core/models';
import { ColorsGridComponent } from '@ppg/shared/color/colors-grid';
import { ColorInfoService } from '@ppg/shared/color/services';

@Component({
  selector: 'ppg-curated-color-palette',
  standalone: true,
  templateUrl: './curated-color-palette.component.html',
  styleUrl: './curated-color-palette.component.scss',
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    class: 'flex w-full justify-content-center',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ColorsGridComponent],
})
export class CuratedColorPaletteComponent {
  public static readonly MAX_COLORS_COUNT = 20;
  readonly #colorInfoService = inject(ColorInfoService);
  readonly content = input.required<CuratedColorPaletteContent>();
  readonly fullWidth = input<boolean>(false);

  readonly headline = computed(() => this.content().headline ?? '');
  readonly subheadline = computed(() => this.content().subheadline ?? '');
  readonly description = computed(() => this.content().description ?? '');
  readonly textAlignment = computed(() => this.content().textAlignment ?? '');
  protected readonly isTitlesPresent = computed(
    () => !!this.content().headline || !!this.content().subheadline || !!this.content().description,
  );
  private readonly numbersEmgItems = computed(
    () =>
      this.content()
        .productNumbersEmg?.filter((item) => item)
        ?.slice(0, CuratedColorPaletteComponent.MAX_COLORS_COUNT) ?? [],
  );
  private readonly colorsNumbers$ = toObservable(this.numbersEmgItems);
  private readonly colorsInfos = toSignal(
    this.colorsNumbers$.pipe(switchMap((colorsIds) => this.#colorInfoService.getColorInfo(colorsIds))),
    { initialValue: [] },
  );

  protected readonly isTextCenter = computed(() => this.textAlignment() === TextAlignment.CENTER);

  readonly colors = computed(() => {
    const fetchedColorsInfos = this.colorsInfos();
    const colorsIds = this.numbersEmgItems();

    return colorsIds.map((colorId) => this.getColorInfo(colorId, fetchedColorsInfos));
  });

  readonly resources = {
    gridEmptyMessage: ResourceItem.COLORS_FAVORITES_EMPTY_MESSAGE_TITLE,
    gridEmptyMessageGoTo: ResourceItem.COLORS_FAVORITES_EMPTY_MESSAGE_CONTENT,
  } as const;

  readonly gridStyles = 'flex md:block p-2 w-full md:w-6 lg:w-6 xl:w-3 md:col-6 lg:col-6 xl:col-3';

  private getColorInfo(colorId: string, fetchedColorsInfos: ColorInfoValid[]): ColorInfoValid | ColorInfoInvalid {
    const fetchedColorInfo = fetchedColorsInfos.find((colorInfo) => colorInfo.productNumber === colorId);

    return fetchedColorInfo ?? new ColorInfoInvalid(colorId, false, undefined);
  }
}
